import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

// Đồng bộ 1 giá trị filter/sort/page với 1 query param trên URL, tên param
// tự prefix "<namespace>_<key>" (namespace = tab key, vd "accounts_page" vs
// "providers_page") - để mỗi tab giữ state riêng, chuyển qua tab khác rồi
// quay lại không bị lệch trang/filter do dùng chung tên param. Param vắng
// mặt (chưa từng set, hoặc bị set === defaultValue - rỗng khỏi URL cho gọn)
// trả về defaultValue.
// Cập nhật NHIỀU param trong 1 lần setSearchParams. Không thể gọi liên tiếp
// nhiều setter của useUrlState() trong cùng 1 handler: updater dạng hàm của
// react-router không được queue như setState - mọi lần gọi đều dựa trên cùng
// 1 `prev` cũ nên lần cuối ghi đè các lần trước (filter không đổi URL).
// `updates`: { key: value }, `defaults`: { key: defaultValue } (value ===
// default hoặc rỗng thì xoá khỏi URL, giống useUrlState).
export function useUrlStateBatch(namespace) {
  const [, setSearchParams] = useSearchParams();
  return useCallback(
    (updates, defaults = {}) => {
      setSearchParams((prev) => {
        const nextParams = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, resolved]) => {
          const paramName = `${namespace}_${key}`;
          if (resolved === defaults[key] || resolved === '' || resolved === null || resolved === undefined) {
            nextParams.delete(paramName);
          } else {
            nextParams.set(paramName, String(resolved));
          }
        });
        return nextParams;
      });
    },
    [namespace, setSearchParams]
  );
}

export default function useUrlState(namespace, key, defaultValue) {
  const paramName = `${namespace}_${key}`;
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(paramName);
  const value = raw === null ? defaultValue : raw;

  const setValue = useCallback(
    (next) => {
      setSearchParams((prev) => {
        const nextParams = new URLSearchParams(prev);
        const resolved = typeof next === 'function' ? next(nextParams.get(paramName) ?? defaultValue) : next;
        if (resolved === defaultValue || resolved === '' || resolved === null || resolved === undefined) {
          nextParams.delete(paramName);
        } else {
          nextParams.set(paramName, String(resolved));
        }
        return nextParams;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paramName, defaultValue]
  );

  return [value, setValue];
}
